/**
 * Step inspector — edits whatever is selected on the canvas.
 *
 * The class field carries a warning rather than a quiet default. An operator
 * who marks a sales piece as `transacional` to get past the electoral window is
 * not making a labelling mistake, they are committing an infraction, and the
 * screen should say so before they do it.
 */

import {
  CAMPOS_CONDICAO,
  CAMPOS_LANDING,
  EVENTOS_COM_LANDING,
  EVENTOS_GATILHO,
  OPERADORES_CONDICAO,
  landingsDaConfig,
  type CampoCondicao,
  type EventoGatilho,
  type Etapa,
} from '@egba-do/shared'

import { Link } from 'react-router-dom'

import { AreaTexto, Aviso, Botao, Campo, Selecao } from '../ui'
import { MiniEmail } from '../templates/MiniEmail'

const NOMES_EVENTO: Record<string, string> = {
  lead_criado: 'Lead criado',
  lead_importado: 'Lead importado da base',
  formulario_enviado: 'Formulário de landing page enviado',
  pagina_visitada: 'Landing page visitada',
  estagio_alterado: 'Estágio do lead alterado',
  manual: 'Disparo manual pelo operador',
}

const NOMES_CAMPO: Record<string, string> = {
  estagio: 'Estágio',
  origem: 'Origem',
  uf: 'UF',
  municipio: 'Município',
  categoriaId: 'Categoria',
  optinEmail: 'Opt-in de e-mail',
  optinWhatsapp: 'Opt-in de WhatsApp',
  temEmail: 'Tem e-mail',
  temTelefone: 'Tem telefone',
  abriuEmail: 'Abriu algum e-mail',
  clicouEmail: 'Clicou em algum e-mail',
  visitouLanding: 'Visitou a landing page',
  preencheuLanding: 'Preencheu o formulário da landing',
}

const NOMES_OPERADOR: Record<string, string> = {
  igual: 'é igual a',
  diferente: 'é diferente de',
  contem: 'contém',
  existe: 'está preenchido',
  nao_existe: 'está vazio',
}

const CLASSES = [
  {
    valor: 'obrigacional',
    texto: 'Obrigacional — avisa sobre uma obrigação legal do destinatário',
    ajuda:
      'É o caso das campanhas da EGBA: a escola precisa publicar a relação de concluintes (Res. CEE 44/2020, Art. 4º). Passa na janela eleitoral.',
  },
  {
    valor: 'transacional',
    texto: 'Transacional — resposta a um ato do destinatário',
    ajuda: 'Comprovante, recibo, confirmação. Não é comunicação de mercado.',
  },
  {
    valor: 'institucional',
    texto: 'Institucional — divulga a EGBA',
    ajuda: '⛔ Bloqueado na janela eleitoral (Lei 9.504/97, art. 73, VI, "b").',
  },
  {
    valor: 'promocional',
    texto: 'Promocional — oferta, desconto, venda',
    ajuda: '⛔ Bloqueado na janela eleitoral (Lei 9.504/97, art. 73, VI, "b").',
  },
]

/** Minutes ⇄ a friendlier pair of number + unit. */
const UNIDADES = [
  { valor: 1, texto: 'minutos' },
  { valor: 60, texto: 'horas' },
  { valor: 1440, texto: 'dias' },
]

export interface LandingResumida {
  id: string
  titulo: string
  slug: string
}

export interface TemplateResumido {
  id: string
  nome: string
  canal: string
  assunto: string | null
  corpoHtml: string | null
  corpoTexto: string
}

export interface InspetorProps {
  etapa: Etapa | null
  problemas: string[]
  somenteLeitura?: boolean
  /** Páginas disponíveis para os filtros de gatilho/condição e o link da ação. */
  landings?: LandingResumida[]
  /** Templates de mensagem para a ação escolher, por canal. */
  templates?: TemplateResumido[]
  onAlterar: (config: Record<string, unknown>) => void
  onRemover: () => void
}

/**
 * Qualquer landing / somente estas — o mesmo seletor para gatilho e condição.
 * Lista vazia significa "qualquer": é o que a régua entende, então é o que a
 * tela mostra, sem um estado intermediário que só existe no navegador.
 */
function SeletorLandings({
  rotulo,
  escolhidas,
  landings,
  somenteLeitura,
  onAlterar,
}: {
  rotulo: string
  escolhidas: string[]
  landings: LandingResumida[]
  somenteLeitura: boolean
  onAlterar: (ids: string[]) => void
}) {
  const restrito = escolhidas.length > 0
  return (
    <div>
      <p className="rotulo">{rotulo}</p>
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            className="size-4"
            checked={!restrito}
            disabled={somenteLeitura}
            onChange={() => onAlterar([])}
          />
          Qualquer landing page
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            className="size-4"
            checked={restrito}
            disabled={somenteLeitura || landings.length === 0}
            onChange={() => landings[0] && onAlterar([landings[0].id])}
          />
          Somente estas:
        </label>
        {restrito && (
          <div className="ml-6 space-y-1">
            {landings.map((lp) => (
              <label key={lp.id} className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  className="size-4"
                  disabled={somenteLeitura}
                  checked={escolhidas.includes(lp.id)}
                  onChange={(e) =>
                    onAlterar(
                      e.target.checked
                        ? [...escolhidas, lp.id]
                        : escolhidas.filter((id) => id !== lp.id),
                    )
                  }
                />
                <span className="min-w-0 truncate" title={`/lp/${lp.slug}`}>
                  {lp.titulo}
                </span>
              </label>
            ))}
            {landings.length === 0 && (
              <p className="text-xs text-slate-400">Nenhuma landing page criada ainda.</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function Inspetor({
  etapa,
  problemas,
  somenteLeitura = false,
  landings = [],
  templates = [],
  onAlterar,
  onRemover,
}: InspetorProps) {
  if (!etapa) {
    return (
      <div className="rounded-xl border border-dashed border-[color:var(--color-borda)] p-6 text-center text-sm text-slate-400">
        Clique numa etapa para editar.
      </div>
    )
  }

  const c = etapa.config
  const set = (chave: string, valor: unknown) => onAlterar({ ...c, [chave]: valor })

  const minutos = Number(c.minutos) || 0
  const unidade = minutos % 1440 === 0 && minutos >= 1440 ? 1440 : minutos % 60 === 0 && minutos >= 60 ? 60 : 1

  const operador = String(c.operador ?? '')
  const precisaValor = operador !== 'existe' && operador !== 'nao_existe'
  const classeAtual = CLASSES.find((x) => x.valor === c.classeComunicacao)

  return (
    <div className="space-y-5 bg-white p-5">
      {problemas.length > 0 && (
        <Aviso tom="perigo" titulo="Esta etapa impede o uso da régua">
          <ul className="list-inside list-disc">
            {problemas.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Aviso>
      )}

      {/* ---------------------------------------------------------- gatilho */}
      {etapa.tipo === 'gatilho' && (
        <div className="space-y-4">
          <Selecao
            rotulo="Quando a régua começa"
            value={String(c.evento ?? '')}
            disabled={somenteLeitura}
            onChange={(e) => set('evento', e.target.value)}
            opcoes={EVENTOS_GATILHO.map((v) => ({ valor: v, texto: NOMES_EVENTO[v] ?? v }))}
            ajuda="Disparo manual é o que o botão “Disparar” da campanha usa."
          />
          {EVENTOS_COM_LANDING.has(String(c.evento ?? '') as EventoGatilho) && (
            <>
              <SeletorLandings
                rotulo="De qual landing page"
                escolhidas={landingsDaConfig(c)}
                landings={landings}
                somenteLeitura={somenteLeitura}
                onAlterar={(ids) => set('landingPageIds', ids.length ? ids : undefined)}
              />
              <p className="text-xs leading-relaxed text-slate-400">
                A restrição vale para quem entra PELA landing (formulário). O botão “Disparar”
                continua inscrevendo o público escolhido — é o operador mandando.
              </p>
            </>
          )}
        </div>
      )}

      {/* ----------------------------------------------------------- espera */}
      {etapa.tipo === 'espera' && (
        <div className="flex gap-3">
          <div className="w-28">
            <Campo
              rotulo="Esperar"
              type="number"
              min={1}
              disabled={somenteLeitura}
              value={String(Math.max(1, Math.round(minutos / unidade)))}
              onChange={(e) => set('minutos', Math.max(1, Number(e.target.value)) * unidade)}
            />
          </div>
          <div className="flex-1">
            <Selecao
              rotulo="Unidade"
              value={String(unidade)}
              disabled={somenteLeitura}
              onChange={(e) =>
                set('minutos', Math.max(1, Math.round(minutos / unidade)) * Number(e.target.value))
              }
              opcoes={UNIDADES.map((u) => ({ valor: String(u.valor), texto: u.texto }))}
              ajuda="Máximo de 180 dias."
            />
          </div>
        </div>
      )}

      {/* --------------------------------------------------------- condição */}
      {etapa.tipo === 'condicao' && (
        <div className="space-y-4">
          <Selecao
            rotulo="Campo"
            value={String(c.campo ?? '')}
            disabled={somenteLeitura}
            onChange={(e) => {
              const campo = e.target.value
              // Campo de landing só entende aconteceu/não aconteceu: ao trocar
              // para ele, um operador de comparação herdado viraria uma regra
              // que o validador rejeita — melhor já corrigir aqui.
              if (CAMPOS_LANDING.has(campo as CampoCondicao)) {
                onAlterar({
                  ...c,
                  campo,
                  operador:
                    c.operador === 'nao_existe' ? 'nao_existe' : 'existe',
                  valor: undefined,
                })
              } else {
                onAlterar({ ...c, campo, landingPageIds: undefined })
              }
            }}
            opcoes={CAMPOS_CONDICAO.map((v) => ({ valor: v, texto: NOMES_CAMPO[v] ?? v }))}
          />
          {CAMPOS_LANDING.has(String(c.campo ?? '') as CampoCondicao) ? (
            <>
              <Selecao
                rotulo="Pergunta"
                value={operador === 'nao_existe' ? 'nao_existe' : 'existe'}
                disabled={somenteLeitura}
                onChange={(e) => set('operador', e.target.value)}
                opcoes={[
                  { valor: 'existe', texto: 'aconteceu' },
                  { valor: 'nao_existe', texto: 'não aconteceu' },
                ]}
              />
              <SeletorLandings
                rotulo="Em qual landing page"
                escolhidas={landingsDaConfig(c)}
                landings={landings}
                somenteLeitura={somenteLeitura}
                onAlterar={(ids) => set('landingPageIds', ids.length ? ids : undefined)}
              />
              <p className="text-xs leading-relaxed text-slate-400">
                “Visitou” conta o clique rastreado dos envios e o formulário enviado; “preencheu”
                conta só o formulário. É registro de acontecimento, não estimativa.
              </p>
            </>
          ) : (
            <>
              <Selecao
                rotulo="Operador"
                value={operador}
                disabled={somenteLeitura}
                onChange={(e) => set('operador', e.target.value)}
                opcoes={OPERADORES_CONDICAO.map((v) => ({ valor: v, texto: NOMES_OPERADOR[v] ?? v }))}
              />
              {precisaValor && (
                <Campo
                  rotulo="Valor"
                  disabled={somenteLeitura}
                  value={String(c.valor ?? '')}
                  onChange={(e) => set('valor', e.target.value)}
                  ajuda="Comparação ignora acento e caixa. Para sim/não use true ou false."
                />
              )}
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- A/B */}
      {etapa.tipo === 'divisao_ab' && (
        <Campo
          rotulo="Percentual no caminho A"
          type="number"
          min={1}
          max={99}
          disabled={somenteLeitura}
          value={String(Number(c.percentualA) || 50)}
          onChange={(e) => set('percentualA', Number(e.target.value))}
          ajuda="A divisão é estável por lead — quem cai em A sempre cai em A."
        />
      )}

      {/* ------------------------------------------------------------- ação */}
      {etapa.tipo === 'acao' && (
        <div className="space-y-4">
          <Selecao
            rotulo="Canal"
            value={String(c.canal ?? '')}
            disabled={somenteLeitura}
            onChange={(e) => set('canal', e.target.value)}
            opcoes={[
              { valor: 'email', texto: 'E-mail' },
              { valor: 'sms', texto: 'SMS' },
              { valor: 'whatsapp', texto: 'WhatsApp' },
            ]}
          />

          {c.canal === 'whatsapp' && (
            <Aviso tom="atencao">
              WhatsApp só sai para quem tem opt-in explícito registrado. Não existe exceção — nem
              para peça obrigacional, nem para administrador.
            </Aviso>
          )}

          <Selecao
            rotulo="Classificação da peça"
            value={String(c.classeComunicacao ?? '')}
            disabled={somenteLeitura}
            onChange={(e) => set('classeComunicacao', e.target.value)}
            opcoes={[{ valor: '', texto: 'Selecione…' }, ...CLASSES.map((x) => ({ valor: x.valor, texto: x.texto }))]}
            ajuda={classeAtual?.ajuda ?? 'Sem classificação o envio é bloqueado.'}
          />

          {/* O conteúdo vem de um template salvo ou é digitado aqui. E-mail
              pede template (HTML de verdade, com remetente e pré-header);
              SMS/WhatsApp funcionam bem digitados — mas aceitam template. */}
          <Selecao
            rotulo="Conteúdo"
            value={String(c.templateId ?? '')}
            disabled={somenteLeitura}
            onChange={(e) => set('templateId', e.target.value || undefined)}
            opcoes={[
              {
                valor: '',
                texto: c.canal === 'email' ? 'Escrever aqui (texto simples)' : 'Digitar aqui',
              },
              ...templates
                .filter((t) => t.canal === String(c.canal ?? 'email'))
                .map((t) => ({ valor: t.id, texto: `Template: ${t.nome}` })),
            ]}
            ajuda={
              c.canal === 'email'
                ? 'Template traz HTML, assunto, remetente e pré-header prontos.'
                : undefined
            }
          />

          {(() => {
            const escolhido = templates.find((t) => t.id === String(c.templateId ?? ''))
            if (!escolhido) return null
            return (
              <div className="space-y-2">
                {escolhido.assunto && (
                  <p className="text-xs text-slate-500">
                    Assunto: <span className="text-slate-800">{escolhido.assunto}</span>
                  </p>
                )}
                {escolhido.corpoHtml ? (
                  <MiniEmail html={escolhido.corpoHtml} altura={260} />
                ) : (
                  <div className="max-w-sm rounded-xl rounded-tl-sm bg-emerald-50 p-3 text-[13px] leading-relaxed whitespace-pre-wrap text-slate-800">
                    {escolhido.corpoTexto}
                  </div>
                )}
                <p className="text-[11px] text-slate-400">
                  Prévia com variáveis de exemplo.{' '}
                  <Link
                    to={`/templates/${escolhido.id}`}
                    className="font-medium text-marca-700 underline"
                  >
                    Editar template
                  </Link>
                </p>
              </div>
            )
          })()}

          {!c.templateId && c.canal === 'email' && (
            <Campo
              rotulo="Assunto"
              disabled={somenteLeitura}
              value={String(c.assunto ?? '')}
              onChange={(e) => set('assunto', e.target.value)}
            />
          )}

          <Selecao
            rotulo="Landing page do link {{linkLanding}}"
            value={String(c.landingPageId ?? '')}
            disabled={somenteLeitura}
            onChange={(e) => set('landingPageId', e.target.value || undefined)}
            opcoes={[
              { valor: '', texto: 'A landing da campanha' },
              ...landings.map((lp) => ({ valor: lp.id, texto: lp.titulo })),
            ]}
            ajuda="Escreva {{linkLanding}} na mensagem e ele vira o endereço da página — já carimbado para medição e retarget."
          />

          {!c.templateId && (
            <AreaTexto
              rotulo="Mensagem"
              rows={8}
              disabled={somenteLeitura}
              value={String(c.corpo ?? '')}
              onChange={(e) => set('corpo', e.target.value)}
              ajuda="Use {{primeiroNome}}, {{nome}}, {{razaoSocial}} e {{linkLanding}}. Campo desconhecido vira vazio."
            />
          )}
        </div>
      )}

      {etapa.tipo === 'fim' && (
        <p className="text-sm text-slate-500">
          Encerra o percurso do lead. Nada é enviado depois desta etapa.
        </p>
      )}

      {!somenteLeitura && (
        <div className="border-t border-[color:var(--color-borda-suave)] pt-4">
          <Botao variante="perigo" onClick={onRemover}>
            Remover etapa
          </Botao>
        </div>
      )}
    </div>
  )
}
